import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSun, faMoon, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

const CustomSunIcon = ({ className, style }) => (
  <svg 
    className={className}
    style={style}
    width="1.6rem" 
    height="1.6rem" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    {/* Central sun circle */}
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    
    {/* Sun rays */}
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {/* Vertical and horizontal rays */}
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </g>
  </svg>
);

const TeamPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      // Only track cursor on non-touch devices
      if (windowWidth > 768) {
        setPosition({ x: e.clientX, y: e.clientY });
        
        if (cursorRef.current) {
          cursorRef.current.style.left = `${e.clientX}px`;
          cursorRef.current.style.top = `${e.clientY}px`;
        }
        
        if (cursorDotRef.current) {
          cursorDotRef.current.style.left = `${e.clientX}px`;
          cursorDotRef.current.style.top = `${e.clientY}px`;
        }
      }
    };

    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [windowWidth]);

  useEffect(() => {
    const createParticles = () => {
      const particleCount = windowWidth > 768 ? 50 : 20; // Fewer particles on mobile
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.width = `${Math.random() * (windowWidth > 768 ? 5 : 3)}px`; // Smaller particles on mobile
        particle.style.height = particle.style.width;
        particle.style.left = `${Math.random() * 100}vw`;
        particle.style.top = `${Math.random() * 100}vh`;
        document.body.appendChild(particle);
      }
    };

    createParticles();
    
    // Re-create particles on window resize
    const handleResize = () => {
      createParticles();
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
      window.removeEventListener('resize', handleResize);
    };
  }, [isDarkMode, windowWidth]);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const themeClass = isDarkMode ? 'dark-theme' : 'light-theme';

  const getImageStyle = (name) => {
    const baseStyle = {
      borderRadius: '50%',
      width: windowWidth <= 768 ? '120px' : '150px', // Even smaller for mobile
      height: windowWidth <= 768 ? '120px' : '150px', // Even smaller for mobile
      objectFit: 'cover',
      marginBottom: '25px',
      backgroundColor: '#f0f0f0',
    };

    if (name === "Youlin Duan") {
      return {
        ...baseStyle,
        objectPosition: 'center 25%',
      };
    } else if (name === "Gautam Mahesh") {
      return {
        ...baseStyle,
        objectPosition: 'center 20%',
      };
    }

    return baseStyle;
  };

  const teamMembers = [
    {
      name: "Rachvik Arora",
      role: "Founder",
      description: "An award-winning author, and science communicator with a strong foundation in research, leadership, and innovation, dedicated to driving the fight against climate change",
      imgUrl: "/images/racvik.jpeg",
      linkedIn: "https://www.linkedin.com/in/rachvik-arora-7441892b4/"
    },
    {
      name: "Chiraayu K Bhat",
      role: "Founder",
      description: "A student driven by a personal mission to restore their neighborhood's natural beauty, combining technical innovation and youth leadership to lead the fight against climate change through sustainable solutions",
      imgUrl: "/images/criyu.jpeg",
      linkedIn: "https://www.linkedin.com/in/chiraayu-k-bhat-862435312/"
    },
    {
      name: "Youlin Duan",
      role: "Secretary",
      description: "Passionate about art and climate advocacy, she blends creativity with environmental consciousness, inspiring meaningful action through her dedication to both",
      imgUrl: "/images/youlin.jpeg",
      linkedIn: "https://www.linkedin.com/in/youlin-duan-71454b318/"
    },
    {
      name: "Pranad Navada",
      role: "Head of Technology",
      description: "A skilled frontend developer and competitive programmer, experienced in creating innovative solutions using modern technologies",
      imgUrl: "/images/pranad.png",
      linkedIn: "https://www.linkedin.com/in/pranad-navada-4a1524307/"
    },
    {
      name: "Vishal Makena",
      role: "Head of Innovation",
      description: "An innovative problem-solver with competitive programming skills and expertise in both frontend and backend development, experienced in using modern technologies",
      imgUrl: "/images/vishal.jpeg",
      linkedIn: "https://www.linkedin.com/in/vishal-makena-1661b6323/"
    },
    {
      name: "Gautam Mahesh",
      role: "Head of Research and Development",
      description: "A passionate biology student and computer science enthusiast with 3+ years of programming experience",
      imgUrl: "/images/gotham.jpeg",
      linkedIn: "https://www.linkedin.com/in/gautam-mahesh-7a3461325/"
    },
    {
      name: "Anusha Bagla",
      role: "Head of Marketing",
      description: "A dynamic leader and changemaker. She combines her deep passion for sustainability, international law, and biology to drive transformative change through vision and action",
      imgUrl: "/images/anusha.jpeg",
      linkedIn: "https://www.linkedin.com/in/anusha-bagla-11813b318/"
    },
    {
      name: "Sakshi Battacharya",
      role: "Head of Volunteering",
      description: "An award-winning essayist, she combines her love for history, art, and politics to inspire meaningful action for a sustainable future",
      imgUrl: "/images/sakshi.jpeg",
      linkedIn: "https://www.linkedin.com/in/sakshi-bhattacharya-6142b2348/"
    },
    {
      name: "Sanchi Jawaria",
      role: "Treasurer",
      description: "An economics enthusiast, artist, and badminton player dedicated to driving environmental change through practical solutions",
      imgUrl: "/images/sanchi.jpeg",
      linkedIn: "https://www.linkedin.com/in/sanchi-jawaria-9b510531a/"
    }
  ];

  const styles = {
    teamSection: {
      textAlign: 'center',
      padding: '0 20px',
      paddingBottom: '50px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: isDarkMode ? 
        'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9))' : 
        'linear-gradient(rgba(244, 244, 244, 0.7), rgba(244, 244, 244, 0.7))',
    },
    heading: {
      margin: '2rem 0',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-dark-green)',
      fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', // Responsive font size
    },
    teamContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '20px', // Reduced gap for mobile
      maxWidth: '1400px',
      margin: '0 auto',
    },
    teamMember: {
      backgroundColor: isDarkMode ? 'rgba(46, 204, 113, 0.1)' : 'rgba(46, 204, 113, 0.1)',
      borderRadius: '15px',
      border: `1px solid ${isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-light-green)'}`,
      boxShadow: 'none',
      padding: windowWidth <= 768 ? '15px' : '20px', // Further reduced padding for mobile
      width: '100%', // Full width on mobile
      maxWidth: '400px', // Max width constraint
      textAlign: 'center',
      transition: 'all 0.5s ease',
      cursor: 'pointer',
      transformStyle: 'preserve-3d',
    },
    memberName: {
      fontSize: 'clamp(1.1em, 4vw, 2em)', // Responsive font size
      marginBottom: '10px',
      color: isDarkMode ? 'var(--dark-primary-green)' : 'var(--light-green)',
      transition: 'transform 0.3s ease',
    },
    memberRole: {
      fontSize: 'clamp(0.9em, 3vw, 1.4em)', // Responsive font size
      marginBottom: '10px',
      color: isDarkMode ? '#888' : 'var(--light-light-green)',
    },
    memberDescription: {
      fontSize: 'clamp(0.8em, 2.5vw, 1.3em)', // Responsive font size
      color: isDarkMode ? '#e0e0e0' : 'var(--light-dark-green)',
      lineHeight: 1.5,
    },
    // Mobile menu styles
    hamburger: {
      display: windowWidth <= 768 ? 'block' : 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
    },
    mobileMenu: {
      display: mobileMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      zIndex: 1000,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mobileMenuClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
    },
    mobileNavLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontSize: '1.5rem',
    },
    mobileNavLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      padding: '10px',
    },
    // Responsive nav
    desktopNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: windowWidth <= 768 ? '10px' : '20px',
      position: 'relative',
      zIndex: 100,
      flexWrap: 'nowrap',
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      fontSize: windowWidth <= 480 ? '0.89rem' : 'clamp(1rem, 3vw, 1.5rem)',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: windowWidth <= 480 ? '200px' : 'none',
    },
    logoText: {
      display: 'inline', // CHANGED: Always show the text
      fontSize: windowWidth <= 360 ? '0.8rem' : 'inherit', // ADDED: Make text smaller on small screens
    },
    logoIcon: {
      marginRight: '8px',
      display: 'inline-block',
    },
    navControls: {
      display: 'flex',
      alignItems: 'center',
      gap: windowWidth <= 480 ? '5px' : '10px',
      flexShrink: 0,
    },
    desktopNavLinks: {
      display: windowWidth <= 768 ? 'none' : 'flex',
      gap: '20px',
    },
    navLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
    },
    themeToggle: {
      background: 'none',
      border: 'none',
      fontSize: '1.2rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
      marginRight: '5px',
    }
  };

  return (
    <div className={themeClass} style={{ backgroundColor: isDarkMode ? '#1a1a1a' : 'var(--light-white)' }}>
      {windowWidth > 768 && (
        <>
          <div ref={cursorRef} className="custom-cursor" />
          <div ref={cursorDotRef} className="cursor-dot" />
        </>
      )}
      
      <div className="bg-overlay"></div>

      {/* Desktop Navigation */}
      <nav style={styles.desktopNav}>
        <Link to="/" style={styles.logoSection}>
          <FontAwesomeIcon icon={faLeaf} style={styles.logoIcon} />
          <span style={styles.logoText}>The Sustainable Formula</span>
        </Link>
        
        <div style={styles.navControls}>
          <div style={styles.desktopNavLinks}>
            <Link to="/team" style={styles.navLink}>Our Team</Link>
            <Link to="/about" style={styles.navLink}>About Us</Link>
            <Link to="/initiatives" style={styles.navLink}>Initiatives</Link>
            <Link to="/donate" style={styles.navLink}>Donate</Link>
          </div>
          
          <button 
            style={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <CustomSunIcon />
            ) : (
              <FontAwesomeIcon icon={faMoon} />
            )}
          </button>
          
          <button 
            style={styles.hamburger} 
            onClick={toggleMobileMenu}
            aria-label="Menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div style={styles.mobileMenu}>
        <button 
          style={styles.mobileMenuClose} 
          onClick={toggleMobileMenu}
          aria-label="Close menu"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div style={styles.mobileNavLinks}>
          <Link 
            to="/team" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Our Team
          </Link>
          <Link 
            to="/about" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            About Us
          </Link>
          <Link 
            to="/initiatives" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Initiatives
          </Link>
          <Link 
            to="/donate" 
            style={styles.mobileNavLink}
            onClick={() => setMobileMenuOpen(false)}
          >
            Donate
          </Link>
        </div>
      </div>

      <section style={styles.teamSection}>
        <h1 style={styles.heading}>Meet Our Team</h1>
        <div style={styles.teamContainer}>
          {teamMembers.map((member) => (
            <div
              key={member.name}
              style={styles.teamMember}
              onClick={() => window.open(member.linkedIn, '_blank')}
              onTouchStart={(e) => {
                if (windowWidth <= 768) {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = isDarkMode ? 
                    '10px 10px 30px rgba(46, 204, 113, 0.3), -10px -10px 30px rgba(46, 204, 113, 0.3)' : 
                    '10px 10px 30px rgba(94, 171, 112, 0.2), -10px -10px 30px rgba(94, 171, 112, 0.2)';
                }
              }}
              onTouchEnd={(e) => {
                if (windowWidth <= 768) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
              onMouseEnter={(e) => {
                if (windowWidth > 768) {
                  e.currentTarget.style.transform = 'translateY(-10px) translateZ(50px)';
                  e.currentTarget.style.boxShadow = isDarkMode ? 
                    '20px 20px 60px rgba(46, 204, 113, 0.5), -20px -20px 60px rgba(46, 204, 113, 0.5)' : 
                    '20px 20px 60px rgba(94, 171, 112, 0.3), -20px -20px 60px rgba(94, 171, 112, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (windowWidth > 768) {
                  e.currentTarget.style.transform = 'translateY(0) translateZ(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <img
                src={member.imgUrl}
                alt={`${member.name} - ${member.role}`}
                style={getImageStyle(member.name)}
                onError={(e) => {
                  e.target.src = "/api/placeholder/150/150";
                }}
              />
              <h2 style={styles.memberName}>{member.name}</h2>
              <p style={styles.memberRole}>{member.role}</p>
              <p style={styles.memberDescription}>{member.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Add this CSS to your component or styleSheet */}
      <style>
        {`
          @media (max-width: 768px) {
            .nav-links {
              display: none;
            }
            
            .custom-cursor, .cursor-dot {
              display: none;
            }
            
            .logo_dark, .logo_light {
              font-size: 1rem;
            }
            
            html, body {
              touch-action: manipulation;
              overflow-x: hidden;
            }
            
            .particle {
              opacity: 0.6;
            }
          }

          @media (max-width: 480px) {
            .logo_dark, .logo_light {
              font-size: 0.9rem;
              max-width: 200px;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

          @media (max-width: 360px) {
            nav {
              padding: 8px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default TeamPage;